import React, { useState, useEffect } from "react";
import {
  useCreateUserMutation,
  useUpdateUserMutation,
} from "../../services/api";
import ErrorMessage from "../../Components/common/ErrorMessage";
import "./UserForm.css";

const IconClose = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ROLE_LEVELS = {
  1: "User",
  2: "User Host",
  3: "Staff",
  4: "Admin",
};

export default function UserForm({ user, onClose, onDelete }) {
  const isEditMode = !!user;

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    roleLevel: "1",
    isActive: true,
  });

  const [validationError, setValidationError] = useState("");

  const [createUser, { isLoading: isCreating, error: createError }] =
    useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating, error: updateError }] =
    useUpdateUserMutation();

  // Load user data in edit mode
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        password: "",
        confirmPassword: "",
        roleLevel: String(user.role?.level || 1),
        isActive: user.isActive !== undefined ? user.isActive : true,
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    if (validationError) setValidationError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError("");

    // Validation
    if (!isEditMode) {
      if (!formData.password) {
        setValidationError("Password is required for new users");
        return;
      }

      if (formData.password.length < 8) {
        setValidationError("Password must be at least 8 characters");
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setValidationError("Passwords do not match");
        return;
      }
    } else {
      // In edit mode, validate password only if provided
      if (formData.password) {
        if (formData.password.length < 8) {
          setValidationError("Password must be at least 8 characters");
          return;
        }

        if (formData.password !== formData.confirmPassword) {
          setValidationError("Passwords do not match");
          return;
        }
      }
    }

    try {
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        roleLevel: parseInt(formData.roleLevel),
        isActive: formData.isActive,
      };

      // Only include password if provided
      if (formData.password) {
        userData.password = formData.password;
      }

      if (isEditMode) {
        await updateUser({ id: user._id, ...userData }).unwrap();
      } else {
        await createUser(userData).unwrap();
      }

      onClose();
    } catch (err) {
      console.error("Failed to save user:", err);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const isLoading = isCreating || isUpdating;
  const error = createError || updateError;

  return (
    <div className="user-form-overlay" onClick={handleOverlayClick}>
      <div className="user-form-modal">
        {/* Header */}
        <div className="user-form-header">
          <h2>{isEditMode ? "Edit User" : "Create New User"}</h2>
          <button
            type="button"
            className="user-form-close"
            onClick={onClose}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="user-form">
          {error && <ErrorMessage error={error} />}
          {validationError && (
            <div className="validation-error">{validationError}</div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter first name"
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">
                Last Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter last name"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">
              Email <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email address"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">
                Password {!isEditMode && <span className="required">*</span>}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={
                  isEditMode
                    ? "Leave blank to keep current password"
                    : "At least 8 characters"
                }
                required={!isEditMode}
              />
              {isEditMode && (
                <small className="form-hint">
                  Leave blank to keep current password
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">
                Confirm Password {!isEditMode && <span className="required">*</span>}
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter password"
                required={!isEditMode || formData.password}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="roleLevel">
                Role <span className="required">*</span>
              </label>
              <select
                id="roleLevel"
                name="roleLevel"
                value={formData.roleLevel}
                onChange={handleChange}
                required
              >
                {Object.entries(ROLE_LEVELS).map(([level, name]) => (
                  <option key={level} value={level}>
                    {name} (Level {level})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                <span>Active User</span>
              </label>
              <small className="form-hint">
                Inactive users cannot log in
              </small>
            </div>
          </div>

          {/* Actions */}
          <div className="user-form-actions">
            {/* Delete button - left side, only in edit mode */}
            {isEditMode && onDelete && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  onDelete(user._id, `${user.firstName} ${user.lastName}`);
                }}
                disabled={isLoading}
              >
                Delete User
              </button>
            )}

            {/* Right side buttons group */}
            <div className="user-form-actions-right">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading
                  ? isEditMode
                    ? "Updating..."
                    : "Creating..."
                  : isEditMode
                  ? "Update User"
                  : "Create User"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
